/**
 * The configuration item form, grouped.
 *
 * The item is the widest form in the tree - seven decisions plus five JSON specs - and in field order
 * it reads as one wall of inputs where the three that decide whether anything runs at all sit
 * between two that almost never change. So it is grouped the way somebody thinks about it: what this
 * is, what it is aimed at, what it does, how you know it worked, and how it behaves when attached.
 *
 * Extracted from the modal because "+ Add New" in the builder opens the same form in a slide-over
 * (§5), and two copies of a form this size is two places for the prerequisite rules to drift. The
 * draft lives with the caller: the modal saves on submit, the slide-over saves and attaches, and
 * neither should have to reach inside the other's state to do it.
 *
 * One deliberate divergence from the layout spec. The spec's TARGET group is a device dropdown, and
 * an item in this schema does not name a device - the *attachment* does, because the same item is
 * routinely attached twice to configure both firewalls, and a device named here would be a name
 * matched at compile time rather than a foreign key. So TARGET holds the kind of box the item is
 * written for, which is what makes an attach able to refuse; and when the form is opened from inside
 * a scenario, the device the attachment will use is asked for here too, with the inline "+ New
 * Device" the spec asks for.
 *
 * TARGET asks for a product as well, and only for a type that is a family. "Cisco appliance" covers
 * FMC, FTD and ISE, they are reached identically, and a REST call written against one is a 404
 * against the other two - so a type alone cannot say whether an item fits a box. Unset means the item
 * suits any product of the type, which is the right answer for anything that is not one product's API.
 *
 * The five specs are forms, not textareas. Each one is described in `support/labSpecSchema` and
 * rendered by `LabSpecBuilder`, so `action_type` is a select rather than a remembered string and
 * `blast_radius` is three named choices rather than a key somebody has to know exists. The JSON is
 * still reachable, and is opened by itself for a spec holding a key the form cannot render - nothing
 * an author wrote is dropped on save.
 *
 * Both reference catalogues are creatable from here. A device type or an execution method that does
 * not exist yet is the commonest reason an item is left half-finished, and the editors open over this
 * form rather than replacing it: `StbModal` stacks by design, and the save invalidates the catalogue
 * query, so the new row is in the select behind it by the time it closes - with this draft intact.
 */

import React, { useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbButton from '../security-toolbox-ui/StbButton';
import {
  LabConfigurationItem,
  LabDeviceType,
  LabExecutionMethod,
  LabDevice,
  labItemMethodConflicts,
  labItemOperationLabel,
  labMethodSummary,
  labSlugify,
  LAB_ITEM_OPERATIONS,
} from '../../support/labService';
import { LabJsonSchema, LAB_JSON_SCHEMAS } from '../../support/labSpecSchema';
import {
  LabBlockerList,
  LabCheckbox,
  LabDerivedKey,
  LabFieldRow,
  LabOptionSelect,
  LabProductSelect,
  LabRowSelect,
  LabStatusTag,
} from './LabShared';
import { LabSpecBuilder, LabSpecContext } from './LabSpecFields';
import { LabChip, LabSearchBox, LabSection } from './LabAdminShared';
import LabDeviceTypeEditorModal from './LabDeviceTypeEditorModal';
import LabExecutionMethodEditorModal from './LabExecutionMethodEditorModal';

/** A new item, before anything has been decided about it. */
export const LAB_ITEM_EMPTY: Partial<LabConfigurationItem> = {
  key: '',
  name: '',
  description: '',
  operation: 'configure',
  options: {},
  action: {},
  validation: {},
  health_check: {},
  completion_criteria: {},
  pre_configure: false,
  prerequisites: [],
  device_type: null,
  product_uid: null,
  product_name: '',
  action_method: null,
  validation_method: null,
  health_method: null,
  completion_method: null,
  order: 0,
  enabled: true,
  metadata: {},
};

/**
 * The five JSON specs, in the order the compile reads them.
 *
 * Four carry a method: the spec says *what*, the method says *how it is carried out*. They render
 * together rather than in a methods block of their own, because "an api_call payload with the
 * Ansible method selected" is a mistake you only see when the two are side by side. `options` has
 * none - it is values, not work.
 *
 * The shape of each spec lives in `support/labSpecSchema`, which is also what the help menus read, so
 * the controls and the documentation cannot disagree. What stays here is the part that is about
 * *this* column on *this* row rather than about the shape: which method pairs with it, and what
 * leaving it empty means for an item.
 */
export const LAB_ITEM_SPEC_FIELDS: {
  field: 'options' | 'action' | 'validation' | 'health_check' | 'completion_criteria';
  method?: 'action_method' | 'validation_method' | 'health_method' | 'completion_method';
  methodLabel?: string;
  methodHelp?: string;
  schema: LabJsonSchema;
  note: string;
}[] = [
  {
    field: 'options',
    schema: LAB_JSON_SCHEMAS.itemOptions,
    note: '{{option}} in any spec below is substituted with the value set here.',
  },
  {
    field: 'action',
    method: 'action_method',
    methodLabel: 'Carried out by',
    methodHelp: 'Unset falls back to a plain shell command.',
    schema: LAB_JSON_SCHEMAS.itemAction,
    note: 'Empty for an item that only checks something.',
  },
  {
    field: 'validation',
    method: 'validation_method',
    methodLabel: 'Checked by',
    methodHelp: 'Must be able to observe state, or this passes on its own exit code.',
    schema: LAB_JSON_SCHEMAS.itemValidation,
    note: 'Runs in the validate phase, or in bootstrap when the item is pre-configured.',
  },
  {
    field: 'health_check',
    method: 'health_method',
    methodLabel: 'Polled by',
    methodHelp: 'Must be able to observe state - usually a port or HTTP probe.',
    schema: LAB_JSON_SCHEMAS.itemHealthCheck,
    note: 'Devices named here are monitored wherever this item is attached.',
  },
  {
    field: 'completion_criteria',
    method: 'completion_method',
    methodLabel: 'Measured by',
    methodHelp: 'Only for completion measured by reaching into the pod. Unset is the common case.',
    schema: LAB_JSON_SCHEMAS.itemCompletion,
    note: "Empty means this item's own steps passing.",
  },
];

/** What the form is being filled in *for*, when it was opened from inside a scenario. */
export type LabItemAttachContext = {
  scenarioName: string;
  /** The devices this scenario has. The attachment lands on one of them. */
  devices: LabDevice[];
  deviceId: number | null;
  onDeviceChange: (deviceId: number | null) => void;
  /** Opens the device form without leaving this one. The spec's inline "+ New Device". */
  onNewDevice?: () => void;
  /**
   * Items already attached to this scenario. The prerequisite picker starts here, because a
   * prerequisite that is not in the scenario is one the attach has to pull in.
   */
  attachedItemIds?: number[];
};

/**
 * Why this item cannot be saved yet.
 *
 * The layout spec asks for Save to be disabled until the device, the action and the method are
 * filled - the three without which nothing runs. Two of those are conditional here rather than
 * absolute, and the difference matters: an item that only *checks* something is a legitimate item,
 * and a pre-configured one is validation-only by design, so demanding an action would refuse the
 * shape the `pre_configure` flag exists for.
 *
 * What is genuinely non-negotiable is that the item does something, and that whatever it does has a
 * way of being carried out. Everything softer than that is a blocker on the row, not a refusal here:
 * an author part-way through swapping a device type must be able to put the form down.
 */
export const labItemFormBlockers = (
  draft: Partial<LabConfigurationItem>,
  attach?: LabItemAttachContext | null,
): string[] => {
  const blockers: string[] = [];
  const action = draft.action || {};
  const validation = draft.validation || {};

  if (!draft.name?.trim()) {
    blockers.push('Give the item a name. The key that compiled steps are named from comes from it.');
  }

  if (!Object.keys(action).length && !Object.keys(validation).length) {
    blockers.push(
      'Either an action or a validation. With neither, the item compiles to nothing and attaching it has no effect on a run.',
    );
  }

  if (Object.keys(action).length && !draft.action_method && !(action as any).action_type) {
    blockers.push(
      'How the action is carried out. Neither a method nor an action_type in the spec means the compile falls back to a plain shell command.',
    );
  }

  if (Object.keys(validation).length && !draft.validation_method && !(validation as any).action_type) {
    blockers.push(
      'How the validation is carried out. Without it, nothing observes whether the work landed.',
    );
  }

  if (attach && !attach.deviceId) {
    blockers.push(
      `Which device in ${attach.scenarioName} this lands on. The device is part of the attachment, so there is no default.`,
    );
  }

  return blockers;
};

export type LabConfigurationItemFormProps = {
  /** The row being edited, or null when creating. Only used for what must not be recomputed. */
  item: LabConfigurationItem | null;
  draft: Partial<LabConfigurationItem>;
  onChange: (patch: Partial<LabConfigurationItem>) => void;

  /** The whole catalogue, for the prerequisite picker and the key clash check. */
  catalogue: LabConfigurationItem[];
  deviceTypes: LabDeviceType[];
  methods: LabExecutionMethod[];
  /**
   * Cisco products, for a device type that is a family.
   *
   * Only fetched by callers that can render the select - the catalogue is two thousand rows, and most
   * items are written for a type that names its own product.
   */
  products?: { id: number; name: string }[];
  productsLoading?: boolean;

  /** Per-field JSON parse errors, held by the caller so the save can refuse on them. */
  jsonErrors: Record<string, string | null>;
  onJsonError: (field: string, error: string | null) => void;

  /** Set when the form was opened from inside a scenario. See `LabItemAttachContext`. */
  attach?: LabItemAttachContext | null;
  /** The blockers the row will carry once saved. Computed by the caller, shown at the foot. */
  rowBlockers?: string[];
};

const LabConfigurationItemForm: React.FC<LabConfigurationItemFormProps> = ({
  item,
  draft,
  onChange,
  catalogue,
  deviceTypes,
  methods,
  products,
  productsLoading,
  jsonErrors,
  onJsonError,
  attach,
  rowBlockers = [],
}) => {
  const [prereqSearch, setPrereqSearch] = useState('');
  /**
   * Whether the prerequisite picker shows the whole catalogue.
   *
   * Off by default when the form knows which scenario it is for, because a prerequisite already in
   * that scenario is free and one from outside it is not: attaching this item will pull that chain
   * in too, and rows nobody asked for appearing in a scenario is the surprise the flag exists to
   * make deliberate. Absent a scenario there is nothing to narrow to, so it is on.
   */
  const [allScenarios, setAllScenarios] = useState(!attach);

  /**
   * Which reference row is being authored over the top of this form, if any.
   *
   * The method editor remembers *which* of the four selects asked for it, so the row it creates is
   * selected there when it closes. Without that the author saves a method and then has to find it in
   * a list of twelve, in the select they were already looking at.
   */
  const [newDeviceType, setNewDeviceType] = useState(false);
  const [newMethodFor, setNewMethodFor] = useState<
    'action_method' | 'validation_method' | 'health_method' | 'completion_method' | null
  >(null);

  /** Derived from the name for a new item; frozen once saved. */
  const generatedKey = useMemo(
    () => (item ? item.key : labSlugify(draft.name || '')),
    [item, draft.name],
  );

  /**
   * Which items may be prerequisites of this one.
   *
   * Itself and anything that already needs it, directly or through a chain, are excluded: the attach
   * pass walks this graph to build the closure, and a cycle there is not a bad ordering but a request
   * that cannot be satisfied at all. Refusing it while authoring beats a 400 from a later attach that
   * names two items nobody was editing.
   */
  const acyclic = useMemo(() => {
    if (!item) return catalogue;

    const dependents = new Set<number>([item.id]);
    let grew = true;

    while (grew) {
      grew = false;
      catalogue.forEach((candidate) => {
        if (dependents.has(candidate.id)) return;
        if ((candidate.prerequisites || []).some((id) => dependents.has(id))) {
          dependents.add(candidate.id);
          grew = true;
        }
      });
    }

    return catalogue.filter((candidate) => !dependents.has(candidate.id));
  }, [catalogue, item]);

  const inScenario = useMemo(() => new Set(attach?.attachedItemIds || []), [attach]);

  const prerequisiteOptions = useMemo(() => {
    const needle = prereqSearch.trim().toLowerCase();

    return acyclic
      .filter((candidate) => allScenarios || !attach || inScenario.has(candidate.id))
      .filter(
        (candidate) =>
          !needle ||
          `${candidate.name} ${candidate.key}`.toLowerCase().includes(needle),
      );
  }, [acyclic, allScenarios, attach, inScenario, prereqSearch]);

  const selectedPrerequisites = useMemo(
    () =>
      (draft.prerequisites || [])
        .map((id) => catalogue.find((candidate) => candidate.id === id) || null)
        .map((row, index) => ({ row, id: (draft.prerequisites || [])[index] })),
    [draft.prerequisites, catalogue],
  );

  const togglePrerequisite = (id: number) => {
    const current = draft.prerequisites || [];

    onChange({
      prerequisites: current.includes(id)
        ? current.filter((entry) => entry !== id)
        : [...current, id],
    });
  };

  const chosenType = useMemo(
    () => deviceTypes.find((candidate) => candidate.id === draft.device_type) || null,
    [deviceTypes, draft.device_type],
  );

  /**
   * Methods this item names that the chosen device type does not allow.
   *
   * A warning here and a refusal at attach time, deliberately: the type may be about to be widened,
   * or the author may be part-way through swapping both.
   */
  const methodConflicts = useMemo(() => {
    if (!chosenType) return [];

    const keyOf = (id?: number | null) =>
      methods.find((candidate) => candidate.id === id)?.key || '';

    // Re-derived rather than taken from the draft: the readable keys are serializer fields, so a
    // select changed in this form leaves them behind and the message would name the old method.
    return labItemMethodConflicts(
      {
        ...(LAB_ITEM_EMPTY as LabConfigurationItem),
        ...draft,
        action_method_key: keyOf(draft.action_method),
        validation_method_key: keyOf(draft.validation_method),
        health_method_key: keyOf(draft.health_method),
        completion_method_key: keyOf(draft.completion_method),
      } as LabConfigurationItem,
      chosenType,
    );
  }, [draft, chosenType, methods]);

  /**
   * The three roles that are scored, filled by a method that cannot observe anything.
   *
   * Worth its own list rather than folding into the blockers: this is the failure that looks like
   * success. A terraform apply used as the validation reports its own exit code, and every learner
   * passes.
   */
  const checkWarnings = useMemo(() => {
    const roles: {
      field: 'validation_method' | 'health_method' | 'completion_method';
      name: string;
    }[] = [
      { field: 'validation_method', name: 'validation' },
      { field: 'health_method', name: 'health check' },
      { field: 'completion_method', name: 'completion' },
    ];

    return roles.flatMap((role) => {
      const method = methods.find((candidate) => candidate.id === draft[role.field]);

      if (!method || method.supports_check) return [];

      return [
        `${method.name} only applies - used as the ${role.name} it reports its own exit code, so this passes whether or not the pod is in the state the item claims.`,
      ];
    });
  }, [methods, draft]);

  /**
   * The keys the specs can offer rather than ask to be typed.
   *
   * Device keys come from the scenario the form was opened in, because that is the only place an
   * honest list exists: a health check names devices by key and the compile resolves those names
   * against whatever scenario the item is attached to. Option keys come from the draft itself, so
   * adding an option makes it selectable in the specs below without a save in between.
   */
  const specContext = useMemo<LabSpecContext>(
    () => ({
      deviceKeys: (attach?.devices || []).map((device) => device.device_key).filter(Boolean),
      optionKeys: Object.keys(draft.options || {}),
    }),
    [attach, draft.options],
  );

  const specField = (field: (typeof LAB_ITEM_SPEC_FIELDS)[number]) => (
    <LabSpecBuilder
      key={field.field}
      schema={field.schema}
      value={draft[field.field]}
      resetKey={item?.id ?? 'new'}
      context={specContext}
      onChange={(value, error) => {
        onJsonError(field.field, error);
        if (!error) onChange({ [field.field]: value } as Partial<LabConfigurationItem>);
      }}
    >
      <p className="m-0 text-xs text-gray-500">{field.note}</p>

      {/* Beside the spec, not in a methods section of its own - the pairing is what is being
          authored, and a mismatched pair is only visible together. */}
      {field.method && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[16rem] flex-1">
            <LabRowSelect
              label={field.methodLabel || 'Method'}
              value={draft[field.method]}
              rows={methods}
              onChange={(value) =>
                onChange({ [field.method as string]: value } as Partial<LabConfigurationItem>)
              }
              labelFor={(method) => `${method.name}${method.enabled ? '' : ' (disabled)'}`}
              hintFor={(method) => labMethodSummary(method)}
              emptyLabel="Not set"
              helpText={field.methodHelp}
              disabled={!methods.length}
            />
          </div>

          {/* The commonest reason this select is left unset is that the method has not been
              authored yet. Opened over this form, so the draft survives the detour. */}
          <StbButton
            role="add"
            size="xsmall"
            onClick={async () => setNewMethodFor(field.method || null)}
          >
            + New method
          </StbButton>
        </div>
      )}
    </LabSpecBuilder>
  );

  const spec = (field: (typeof LAB_ITEM_SPEC_FIELDS)[number]['field']) => {
    const entry = LAB_ITEM_SPEC_FIELDS.find((candidate) => candidate.field === field);

    return entry ? specField(entry) : null;
  };

  const attachmentCount = item?.attachment_count ?? 0;

  return (
    <div className="flex flex-col gap-4">
      {/* An edit here lands in every scenario that attached it, on their next compile. Said up
          front rather than after the fact, because the catalogue is shared on purpose and the count
          is the only thing that says how far a change reaches. */}
      {attachmentCount > 0 && (
        <p className="m-0 text-sm text-amber-800">
          {attachmentCount === 1
            ? 'One scenario already uses this item.'
            : `${attachmentCount} scenarios already use this item.`}{' '}
          Changes reach all of them on their next compile.
        </p>
      )}

      <LabSection title="What this is" hint="The catalogue entry.">
        <TextField
          label="Name"
          value={draft.name || ''}
          onChange={(evt) => onChange({ name: evt.target.value })}
          helperText="What an author picks it out of the catalogue by."
          fullWidth
        />

        <LabDerivedKey
          value={generatedKey}
          frozen={!!item}
          usage="Compiled steps are named from it."
        />

        <TextField
          label="Description"
          value={draft.description || ''}
          onChange={(evt) => onChange({ description: evt.target.value })}
          helperText="What this does to a pod."
          multiline
          minRows={2}
          fullWidth
        />

        <LabOptionSelect
          label="Operation"
          value={draft.operation}
          options={LAB_ITEM_OPERATIONS}
          onChange={(operation) => onChange({ operation })}
          helpText="What class of change this makes. The catalogue is filtered by it."
        />
      </LabSection>

      <LabSection
        title="Target"
        hint={
          attach
            ? `Which box this lands on in ${attach.scenarioName}, and what kind of box it is written for. The device belongs to the attachment; the type travels with the item.`
            : 'What kind of box this is written for. Which device it lands on is decided when it is attached.'
        }
        actions={
          <>
            {attach?.onNewDevice && (
              <StbButton role="add" size="xsmall" onClick={async () => attach.onNewDevice?.()}>
                + New device
              </StbButton>
            )}
            <StbButton role="add" size="xsmall" onClick={async () => setNewDeviceType(true)}>
              + New device type
            </StbButton>
          </>
        }
      >
        {/* Only when the form knows the scenario. Out of that context there is nothing honest to
            put here: an item does not name a device, and offering every device in the estate would
            invite picking one from a scenario this item is not in. */}
        {attach && (
          <LabRowSelect
            label={`Device in ${attach.scenarioName}`}
            value={attach.deviceId}
            rows={attach.devices}
            onChange={attach.onDeviceChange}
            labelFor={(device) => device.display_name || device.device_key}
            hintFor={(device) =>
              [
                device.device_type_name || 'unclassified',
                device.address_template || 'no address',
              ].join(' • ')
            }
            allowEmpty
            emptyLabel="Pick the device"
            helpText="Attaching the same item to a second device is how both firewalls get configured."
          />
        )}

        {attach && !attach.devices.length && (
          <p className="m-0 text-sm text-amber-800">
            {attach.scenarioName} has no devices yet, and an attachment names one. Add one with the
            button above - this form is not lost.
          </p>
        )}

        {/* The product is cleared with the type, existing item included: it is the one field the old
            type makes false rather than merely stale, and an FMC id under an FTD-only type is an item
            claiming a spec it was not written against. */}
        <LabRowSelect
          label="Device type this is written for"
          value={draft.device_type}
          rows={deviceTypes}
          onChange={(device_type) => onChange({ device_type, product_uid: null, product_name: '' })}
          labelFor={(type) => `${type.name}${type.enabled ? '' : ' (disabled)'}`}
          hintFor={(type) =>
            type.requires_product
              ? 'A family - pick the product below'
              : type.product_name || undefined
          }
          emptyLabel="Any kind of device"
          helpText="Refused at attach time against the device. Unset means nothing is checked."
        />

        {/* The item's own product, asked for exactly when the type stops being an answer. Compared
            with the device's at attach time, so an item written for FMC cannot land on an FTD. */}
        {!!chosenType?.requires_product && (
          <LabProductSelect
            value={draft.product_uid}
            name={draft.product_name}
            products={products}
            loading={productsLoading}
            onChange={(product) =>
              onChange({ product_uid: product?.id ?? null, product_name: product?.name || '' })
            }
            helpText={`${chosenType.name} covers several products with different APIs. Set this when the spec below is one product's; leave it unset for work the whole family takes.`}
          />
        )}

        {!deviceTypes.length && (
          <p className="m-0 text-sm text-gray-600">
            No device types authored yet. Add them in the reference catalogue, or run
            seed_lab_reference_data.
          </p>
        )}

        {chosenType && (
          <p className="m-0 text-xs text-gray-500">
            {chosenType.default_transport}
            {chosenType.default_port ? `:${chosenType.default_port}` : ''}
            {(chosenType.method_keys || []).length
              ? ` • allows ${(chosenType.method_keys || []).join(', ')}`
              : ' • any method'}
          </p>
        )}

        {!!methodConflicts.length && (
          <ul className="m-0 list-disc pl-5 text-sm text-amber-800">
            {methodConflicts.map((conflict) => (
              <li key={conflict}>{conflict}</li>
            ))}
          </ul>
        )}
      </LabSection>

      <LabSection
        title="Action"
        hint="What the item does, and how. Empty for an item that only checks something."
      >
        {spec('options')}
        {spec('action')}

        {!methods.length && (
          <p className="m-0 text-sm text-gray-600">
            No execution methods authored yet, so each spec falls back to whatever it states itself.
          </p>
        )}
      </LabSection>

      <LabSection
        title="Verification"
        hint="How you know it worked. Every method here has to be able to observe state."
      >
        {spec('validation')}
        {spec('health_check')}
        {spec('completion_criteria')}

        {!!checkWarnings.length && (
          <ul className="m-0 list-disc pl-5 text-sm text-amber-800">
            {checkWarnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        )}
      </LabSection>

      {/* Foldable, and the only group here that is: every field in it has a working default, which is
          the rule `LabSection` states for the affordance. */}
      <LabSection
        title="Behaviour"
        hint="What has to happen before it, whether the pod already arrives with it done, and where it sits."
        collapsible
        defaultOpen
      >
        <LabCheckbox
          label="Pods arrive with this already applied"
          checked={!!draft.pre_configure}
          onChange={(pre_configure) => onChange({ pre_configure })}
          helpText="No setup step is generated. The check still runs, in bootstrap, so a template that did not really arrive with it fails loudly."
        />

        {/* Not a caveat about a checkbox: the pairing is the one combination that quietly ruins a
            lab, because the learner is handed the answer and nothing reports a fault. */}
        {draft.pre_configure && draft.operation === 'simulate' && (
          <p className="m-0 text-sm text-amber-800">
            A simulated problem pre-built into the template is the answer, already in the pod.
            Nothing will report this as wrong.
          </p>
        )}

        {/* Chips rather than a list of ticks, because the selection is what has to be readable: a
            seven-prerequisite item is a chain somebody has to be able to see at a glance, and each
            chip carries its own remove. */}
        <div className="rounded-md border border-gray-200 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-semibold text-gray-700">Runs after</span>
            <LabStatusTag
              label={`${(draft.prerequisites || []).length} selected`}
              sentiment={(draft.prerequisites || []).length ? 'info' : 'warning'}
            />
          </div>

          <p className="m-0 mt-1 text-xs text-gray-500">
            Items that must be configured before this one. Attaching this attaches them too, and
            their own prerequisites with them.
          </p>

          {!!selectedPrerequisites.length && (
            <div className="mt-2 flex flex-wrap gap-1">
              {selectedPrerequisites.map(({ row, id }) => (
                <LabChip
                  key={id}
                  label={row?.name || `item ${id}`}
                  hint={row ? `${row.key} • ${labItemOperationLabel(row.operation)}` : undefined}
                  implied={!!attach && !inScenario.has(id)}
                  onRemove={() => togglePrerequisite(id)}
                />
              ))}
            </div>
          )}

          {!!attach && (
            <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={allScenarios}
                onChange={(evt) => setAllScenarios(evt.target.checked)}
              />
              <span>
                Include items from other scenarios. Anything picked from outside{' '}
                {attach.scenarioName} is attached to it too when this is.
              </span>
            </label>
          )}

          <div className="mt-2">
            <LabSearchBox
              value={prereqSearch}
              onChange={setPrereqSearch}
              placeholder="Search the catalogue"
            />
          </div>

          {!prerequisiteOptions.length ? (
            <p className="m-0 mt-2 text-xs text-gray-500">
              {prereqSearch.trim()
                ? 'Nothing matches that.'
                : attach && !allScenarios
                  ? `Nothing else is attached to ${attach.scenarioName} yet. Tick the box above to pick from the whole catalogue.`
                  : 'Nothing else in the catalogue can be a prerequisite of this item.'}
            </p>
          ) : (
            <div className="mt-2 max-h-56 overflow-y-auto rounded border border-gray-100">
              {prerequisiteOptions.map((candidate) => (
                <label
                  key={candidate.id}
                  className="flex items-start gap-2 border-b border-gray-100 px-2 py-1.5 text-sm last:border-b-0 hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={(draft.prerequisites || []).includes(candidate.id)}
                    onChange={() => togglePrerequisite(candidate.id)}
                  />
                  <span>
                    <span className="font-medium">{candidate.name}</span>
                    <span className="ml-2 font-mono text-xs text-gray-500">{candidate.key}</span>
                    <span className="block text-xs text-gray-500">
                      {labItemOperationLabel(candidate.operation)}
                      {candidate.pre_configure ? ' • already in the template' : ''}
                      {candidate.enabled ? '' : ' • disabled'}
                      {attach && !inScenario.has(candidate.id)
                        ? ` • not in ${attach.scenarioName} yet`
                        : ''}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          )}

          {/* Excluded rather than offered and refused: the attach pass cannot satisfy a cycle at
              all, so there is nothing useful to do with the choice. */}
          {!!item && acyclic.length < catalogue.length - 1 && (
            <p className="m-0 mt-2 text-xs text-gray-500">
              Items that already need this one, directly or through a chain, are not listed - that
              would be a cycle no attach could resolve.
            </p>
          )}
        </div>

        <LabFieldRow>
          <TextField
            label="Order"
            type="number"
            value={draft.order ?? 0}
            onChange={(evt) =>
              onChange({ order: evt.target.value === '' ? 0 : Number(evt.target.value) })
            }
            helperText="Default position in a scenario. Prerequisites win when the two disagree."
            fullWidth
          />
          <LabCheckbox
            label="Enabled"
            checked={!!draft.enabled}
            onChange={(enabled) => onChange({ enabled })}
            helpText="Disabled retires the item: it stays attached everywhere and compiles to nothing."
          />
        </LabFieldRow>

        <LabSpecBuilder
          schema={LAB_JSON_SCHEMAS.itemMetadata}
          value={draft.metadata}
          resetKey={item?.id ?? 'new'}
          context={specContext}
          onChange={(metadata, error) => {
            onJsonError('metadata', error);
            if (!error) onChange({ metadata });
          }}
        />
      </LabSection>

      {/* Shown while there is still a form to fix, not only in the list afterwards. */}
      <LabBlockerList
        blockers={rowBlockers}
        heading="Once compiled, this item will not do what it looks like it does:"
      />

      {/* Surfaced so a JSON error is not silently the reason Save does nothing. */}
      {Object.entries(jsonErrors)
        .filter(([, error]) => !!error)
        .map(([field, error]) => (
          <p className="m-0 text-sm text-amber-800" key={field}>
            {`${field}: ${error}`}
          </p>
        ))}

      {/* Both stack over this form rather than replacing it. The new row is selected on the way
          back, because the reason anybody opened these was the select they were looking at. */}
      <LabDeviceTypeEditorModal
        open={newDeviceType}
        deviceType={null}
        onClose={() => setNewDeviceType(false)}
        onSaved={(deviceType) => onChange({ device_type: deviceType.id })}
      />

      <LabExecutionMethodEditorModal
        open={!!newMethodFor}
        method={null}
        onClose={() => setNewMethodFor(null)}
        onSaved={(method) => {
          if (newMethodFor) {
            onChange({ [newMethodFor]: method.id } as Partial<LabConfigurationItem>);
          }
        }}
      />
    </div>
  );
};

export default LabConfigurationItemForm;
